import logger from '../../base/media/logger';
import { safePlayMediaElement } from '../../base/media/playbackUtils';
import {
    CLEAR_INTERVAL,
    INTERVAL_TIMEOUT,
    SET_INTERVAL,
    timerWorkerScript
} from '../presenter/TimeWorker';

import { getCropRect, getSoftwareZoom } from './CaptureZoomState';

/**
 * HTMLMediaElement.HAVE_CURRENT_DATA: the first readyState at which drawImage
 * copies a decoded frame instead of silently painting nothing.
 */
const HAVE_CURRENT_DATA = 2;

const DEFAULT_FRAME_RATE = 30;

/**
 * Crops the center of a local video track and scales it back to the original
 * size, so remote participants receive the same zoomed framing the local user
 * sees. It is installed only while a crop is actually needed - a camera whose
 * hardware zoom covers the request, or a 1x target, does not pay for it.
 *
 * The crop factor is not stored here: it is polled from
 * {@link CaptureZoomState} every frame so pinching never has to touch the
 * effect, and so the same value drives the virtual background effect when
 * that one owns the track instead.
 */
export default class CameraZoomEffect {
    _canvas: HTMLCanvasElement;
    _ctx: CanvasRenderingContext2D;
    _frameRate: number;
    _lastWarning: number;
    _stream: ?MediaStream;
    _video: HTMLVideoElement;
    _videoType: string;
    _worker: ?Worker;

    /**
     * Initializes a new {@code CameraZoomEffect} instance.
     *
     * @param {string} videoType - 'camera' or 'desktop'; selects both the
     * tracks this effect accepts and the crop target it follows.
     */
    constructor(videoType: string) {
        this._videoType = videoType;
        this._canvas = document.createElement('canvas');
        this._ctx = this._canvas.getContext('2d');
        this._video = document.createElement('video');
        this._video.autoplay = true;
        this._video.muted = true;
        this._video.playsInline = true;
        this._lastWarning = 0;

        this._onTimer = this._onTimer.bind(this);
    }

    /**
     * Checks if the local track supports this effect.
     *
     * @param {Object} mhLocalTrack - Track to apply the effect to.
     * @returns {boolean}
     */
    isEnabled(mhLocalTrack: Object) {
        return mhLocalTrack.isVideoTrack() && mhLocalTrack.videoType === this._videoType;
    }

    /**
     * Returns the video type this effect was created for.
     *
     * @returns {string}
     */
    getVideoType() {
        return this._videoType;
    }

    /**
     * Starts drawing cropped frames of the stream into a canvas.
     *
     * @param {MediaStream} stream - Stream to be used for processing.
     * @returns {MediaStream} - The stream with the applied effect.
     */
    startEffect(stream: MediaStream) {
        const track = stream.getVideoTracks()[0];
        const { frameRate, height, width } = track?.getSettings?.() ?? {};

        this._stream = stream;
        this._frameRate = parseInt(frameRate, 10) || DEFAULT_FRAME_RATE;
        this._canvas.width = parseInt(width, 10) || 2;
        this._canvas.height = parseInt(height, 10) || 2;
        this._video.srcObject = stream;
        this._play();

        this._worker = new Worker(timerWorkerScript, { name: 'Capture zoom worker' });
        this._worker.onmessage = this._onTimer;
        this._worker.postMessage({
            id: SET_INTERVAL,
            timeMs: 1000 / this._frameRate
        });

        return this._canvas.captureStream(this._frameRate);
    }

    /**
     * Stops the render loop. The same instance is restarted on unmute, so
     * only per-stream state is released.
     *
     * @returns {void}
     */
    stopEffect() {
        if (this._worker) {
            this._worker.postMessage({ id: CLEAR_INTERVAL });
            this._worker.terminate();
            this._worker = undefined;
        }
        this._video.srcObject = null;
        this._stream = undefined;
    }

    /**
     * Timer worker message handler.
     *
     * @param {MessageEvent} response - The worker message.
     * @private
     * @returns {void}
     */
    _onTimer(response) {
        if (response.data.id === INTERVAL_TIMEOUT) {
            this._render();
        }
    }

    /**
     * Draws one cropped frame. Skips, rather than paints black, while the
     * video element has nothing decoded yet.
     *
     * @private
     * @returns {void}
     */
    _render() {
        const video = this._video;

        if (!this._stream) {
            return;
        }

        if (video.readyState < HAVE_CURRENT_DATA) {
            this._warnThrottled(`Capture zoom: no decoded frame yet (readyState=${video.readyState})`);
            this._play();

            return;
        }

        const width = video.videoWidth;
        const height = video.videoHeight;

        if (!(width > 0) || !(height > 0)) {
            return;
        }

        // A shared window can be resized mid-share; only reassigning on change
        // avoids clearing the drawing surface every frame.
        if (this._canvas.width !== width || this._canvas.height !== height) {
            this._canvas.width = width;
            this._canvas.height = height;
        }

        const { sx, sy, sw, sh } = getCropRect(width, height, getSoftwareZoom(this._videoType));

        try {
            this._ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);
        } catch (error) {
            this._warnThrottled('Capture zoom frame failed; dropping it and continuing', error);
        }
    }

    /**
     * Starts the hidden video element; autoplay alone is not enough on Safari.
     *
     * @private
     * @returns {void}
     */
    _play() {
        safePlayMediaElement(this._video, {
            allowDetached: true,
            onAutoplayBlocked: error => this._warnThrottled('Capture zoom video blocked by autoplay policy', error),
            onError: error => this._warnThrottled('Capture zoom video failed to play', error)
        });
    }

    /**
     * Logs at most one warning per 10s; every caller sits in the frame loop.
     *
     * @param {string} message - What went wrong.
     * @param {Error} [error] - Optional underlying error.
     * @private
     * @returns {void}
     */
    _warnThrottled(message, error) {
        const now = Date.now();

        if (now - this._lastWarning > 10000) {
            this._lastWarning = now;
            error ? logger.warn(message, error) : logger.warn(message);
        }
    }
}
